import React, { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import SeoHead from "../components/SeoHead";

function Contact() {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("");
  const [statusIsError, setStatusIsError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [captchaQuestion, setCaptchaQuestion] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaAnswer, setCaptchaAnswer] = useState("");

  const loadCaptcha = useCallback(async ({ keepStatus = false } = {}) => {
    try {
      const res = await fetch("/api/contact/captcha");
      let data = {};
      try {
        data = await res.json();
      } catch {
        throw new Error("Invalid captcha response");
      }

      if (!res.ok || !data.question || !data.captchaToken) {
        throw new Error(data.error || "Failed to load captcha");
      }

      setCaptchaQuestion(data.question);
      setCaptchaToken(data.captchaToken);
      setCaptchaAnswer("");
      if (!keepStatus) {
        setStatus("");
        setStatusIsError(false);
      }
    } catch {
      setCaptchaQuestion("");
      setCaptchaToken("");
      setStatusIsError(true);
      setStatus("Unable to load captcha. Ensure the backend is running, then click ↻ to retry.");
    }
  }, []);

  useEffect(() => {
    loadCaptcha();
  }, [loadCaptcha]);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("");
    setStatusIsError(false);

    const trimmedName = formData.name.trim();
    const trimmedEmail = formData.email.trim();
    const trimmedMessage = formData.message.trim();

    if (!trimmedName || !trimmedEmail || !trimmedMessage) {
      setStatusIsError(true);
      setStatus("All fields are required.");
      return;
    }

    if (!captchaAnswer.trim()) {
      setStatusIsError(true);
      setStatus("Please solve the math captcha.");
      return;
    }

    setSubmitting(true);

    try {
      const res = await fetch("/api/contact-submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: trimmedName,
          email: trimmedEmail,
          message: trimmedMessage,
          captchaToken,
          captchaAnswer: captchaAnswer.trim(),
        }),
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (res.ok) {
        setStatusIsError(false);
        setStatus(data.message || "Message sent successfully.");
        setFormData({ name: "", email: "", message: "" });
        await loadCaptcha({ keepStatus: true });
      } else {
        setStatusIsError(true);
        setStatus(data.error || "Failed to send message.");
        await loadCaptcha({ keepStatus: true });
      }
    } catch (err) {
      console.error("Error:", err);
      setStatusIsError(true);
      setStatus("Server error. Please try again.");
      await loadCaptcha({ keepStatus: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <SeoHead
        title="Contact Ghost Inbox – Support"
        description="Questions about Ghost Inbox temporary inboxes, billing, or your account? Contact our team for support, feedback, or partnership inquiries."
        path="/contact"
      />

      <div className="mx-auto max-w-md px-6 py-16">
        <h1 className="text-2xl font-bold text-white">Contact Us</h1>
        <p className="mt-2 text-sm text-zinc-400">
          We typically respond within 1–2 business days. All fields are required.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-4" noValidate>
          <div>
            <label htmlFor="contact-name" className="mb-1 block text-xs font-medium text-zinc-400">
              Name <span className="text-red-400">*</span>
            </label>
            <input
              id="contact-name"
              name="name"
              type="text"
              placeholder="Your name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-4 py-2.5 text-white placeholder-zinc-500 focus:border-emerald-500 focus:outline-none"
            />
          </div>
          <div>
            <label htmlFor="contact-email" className="mb-1 block text-xs font-medium text-zinc-400">
              Email <span className="text-red-400">*</span>
            </label>
            <input
              id="contact-email"
              name="email"
              type="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-4 py-2.5 text-white placeholder-zinc-500 focus:border-emerald-500 focus:outline-none"
            />
          </div>
          <div>
            <label htmlFor="contact-message" className="mb-1 block text-xs font-medium text-zinc-400">
              Message <span className="text-red-400">*</span>
            </label>
            <textarea
              id="contact-message"
              name="message"
              placeholder="How can we help?"
              value={formData.message}
              onChange={handleChange}
              required
              minLength={10}
              className="h-32 w-full resize-none rounded-lg border border-zinc-700 bg-zinc-900 px-4 py-2.5 text-white placeholder-zinc-500 focus:border-emerald-500 focus:outline-none"
            />
          </div>

          <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-4">
            <label htmlFor="contact-captcha" className="mb-2 block text-xs font-medium text-zinc-400">
              Verification <span className="text-red-400">*</span>
            </label>
            <p className="text-sm text-zinc-300">
              What is{" "}
              <span className="font-mono text-base font-semibold text-emerald-400">
                {captchaQuestion || "…"}
              </span>
              ?
            </p>
            <div className="mt-3 flex gap-2">
              <input
                id="contact-captcha"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Your answer"
                value={captchaAnswer}
                onChange={(e) => setCaptchaAnswer(e.target.value.replace(/\D/g, ""))}
                required
                className="w-full rounded-lg border border-zinc-700 bg-zinc-950 px-4 py-2.5 text-white placeholder-zinc-500 focus:border-emerald-500 focus:outline-none"
              />
              <button
                type="button"
                onClick={loadCaptcha}
                className="shrink-0 rounded-lg border border-zinc-700 px-3 text-sm text-zinc-400 hover:border-zinc-500 hover:text-zinc-200"
                title="New captcha"
                aria-label="Generate new captcha"
              >
                ↻
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting || !captchaToken}
            className="w-full rounded-lg bg-emerald-500 py-3 text-sm font-semibold text-zinc-950 hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "Sending…" : "Send Message"}
          </button>
        </form>
        {status && (
          <p
            className={`mt-4 text-center text-sm ${
              statusIsError ? "text-red-400" : "text-emerald-400"
            }`}
          >
            {status}
          </p>
        )}
      </div>
    </Layout>
  );
}

export default Contact;
